import React, { useMemo, useState } from 'react';
import { Formik, Field, Form } from 'formik';
import {
  Alert,
  Button,
  Card,
  Col,
  Form as BsForm,
  Row,
  Spinner,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';

import TabelaDinamica from '~/Components/TabelaDinamica/Tabela';
import MoneyField from '~/Components/Form/MoneyField';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import {
  calcularSaldoFechamentoCaixa,
  cancelarProcessoFechamentoCaixa,
} from '~/Services/Service';

const LIMITE_CHEQUE = 100000;

const extrairMensagemErro = (err, fallback) => {
  if (err && typeof err === 'object' && err.message) return err.message;
  return fallback;
};

const formatarNumero = (valor) => {
  const n = Number(valor || 0);
  return n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatarDataMov = (valor) => {
  if (!valor) return '';
  const d = moment(valor);
  return d.isValid() ? d.format('DD/MM/YYYY') : '';
};

const SecaoMovimentos = ({
  filtros,
  movimentos,
  valoresIniciais,
  onCalculadoSucesso,
  onCancelado,
  carregando,
}) => {
  const ehCheque = filtros && filtros.codigo_ativo > LIMITE_CHEQUE;
  const [calculando, setCalculando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [showCancelarConfirm, setShowCancelarConfirm] = useState(false);

  const dadosTabela = useMemo(
    () =>
      (movimentos || []).map((m, idx) => ({
        ...m,
        id: `${m.data_negocio}-${m.tipo}-${m.indice != null ? m.indice : idx}`,
        data_negocio_fmt: formatarDataMov(m.data_negocio),
        quantidade_fmt: formatarNumero(m.quantidade),
      })),
    [movimentos]
  );

  const colunas = [
    {
      title: 'Data',
      key: 'data_negocio_fmt',
      ordenacao: false,
      width: '110px',
    },
    {
      title: 'Quantidade',
      key: 'quantidade_fmt',
      ordenacao: false,
      align: 'right',
      width: '140px',
    },
    {
      title: 'Tipo',
      key: 'tipo',
      ordenacao: false,
      width: '80px',
    },
    {
      title: 'Observação',
      key: 'observacao',
      ordenacao: false,
    },
  ];

  const initValues = valoresIniciais || {
    dinheiro: 0,
    moeda: 0,
    vale: 0,
    cheque: 0,
  };

  const handleCalcular = (values, { setSubmitting }) => {
    setCalculando(true);
    const payload = {
      codigo_grupo_empresa: filtros.codigo_grupo_empresa,
      codigo_conta: filtros.codigo_conta,
      codigo_ativo: filtros.codigo_ativo,
    };
    calcularSaldoFechamentoCaixa(payload)
      .then((res) => {
        const saldo = Number(res && res.saldo != null ? res.saldo : 0);

        const dinheiro = ehCheque ? 0 : Number(values.dinheiro || 0);
        const moedaVal = ehCheque ? 0 : Number(values.moeda || 0);
        const vale = ehCheque ? 0 : Number(values.vale || 0);
        const cheque = ehCheque ? Number(values.cheque || 0) : 0;
        const totalDigitado = dinheiro + moedaVal + vale + cheque;
        const diferenca = totalDigitado - saldo;

        onCalculadoSucesso({
          valoresDigitados: { dinheiro, moeda: moedaVal, vale, cheque },
          totalDigitado,
          saldoConta: saldo,
          diferenca,
        });
        toast.success('Cálculo realizado com sucesso');
      })
      .catch((err) => {
        toast.error(extrairMensagemErro(err, 'Erro ao calcular saldo'));
      })
      .finally(() => {
        setCalculando(false);
        setSubmitting(false);
      });
  };

  const handleCancelarConfirm = (confirmado) => {
    setShowCancelarConfirm(false);
    if (!confirmado) return;

    setCancelando(true);
    const payload = {
      codigo_grupo_empresa: filtros.codigo_grupo_empresa,
      codigo_conta: filtros.codigo_conta,
      codigo_ativo: filtros.codigo_ativo,
    };
    cancelarProcessoFechamentoCaixa(payload)
      .then(() => {
        toast.success('Processo cancelado com sucesso');
        onCancelado();
      })
      .catch((err) => {
        toast.error(extrairMensagemErro(err, 'Erro ao cancelar processo'));
      })
      .finally(() => {
        setCancelando(false);
      });
  };

  return (
    <Card className="mb-3">
      <Card.Header>
        <strong>Movimentações do Período</strong>
      </Card.Header>
      <Card.Body>
        {carregando ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" /> Carregando movimentações...
          </div>
        ) : dadosTabela.length === 0 ? (
          <Alert variant="info" className="mb-3">
            Nenhuma movimentação encontrada para o período selecionado.
          </Alert>
        ) : (
          <div className="mb-3" style={{ overflowX: 'auto' }}>
            <TabelaDinamica
              colunas={colunas}
              dados={dadosTabela}
              striped
              hover
              fontSize="13px"
            />
          </div>
        )}

        <Formik
          initialValues={initValues}
          onSubmit={handleCalcular}
          enableReinitialize
        >
          {({ values, isSubmitting }) => (
            <Form>
              <Row>
                <Col xs={12} sm={6} md={3} className="mb-2">
                  <BsForm.Group controlId="dinheiro">
                    <small>Dinheiro</small>
                    <Field
                      name="dinheiro"
                      component={MoneyField}
                      className="form-control form-control-sm"
                      disabled={ehCheque}
                    />
                  </BsForm.Group>
                </Col>
                <Col xs={12} sm={6} md={3} className="mb-2">
                  <BsForm.Group controlId="moeda">
                    <small>Moeda</small>
                    <Field
                      name="moeda"
                      component={MoneyField}
                      className="form-control form-control-sm"
                      disabled={ehCheque}
                    />
                  </BsForm.Group>
                </Col>
                <Col xs={12} sm={6} md={3} className="mb-2">
                  <BsForm.Group controlId="vale">
                    <small>Vale</small>
                    <Field
                      name="vale"
                      component={MoneyField}
                      className="form-control form-control-sm"
                      disabled={ehCheque}
                    />
                  </BsForm.Group>
                </Col>
                <Col xs={12} sm={6} md={3} className="mb-2">
                  <BsForm.Group controlId="cheque">
                    <small>Cheque</small>
                    <Field
                      name="cheque"
                      component={MoneyField}
                      className="form-control form-control-sm"
                      disabled={!ehCheque}
                    />
                  </BsForm.Group>
                </Col>
              </Row>

              <div className="d-flex gap-2 flex-wrap mt-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={isSubmitting || calculando || cancelando}
                >
                  {calculando ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-1"
                      />
                      Calculando...
                    </>
                  ) : (
                    'Calcular'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline-danger"
                  size="sm"
                  disabled={calculando || cancelando}
                  onClick={() => setShowCancelarConfirm(true)}
                >
                  {cancelando ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-1"
                      />
                      Cancelando...
                    </>
                  ) : (
                    'Cancelar Processo'
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Card.Body>

      <ModalConfirmacao
        show={showCancelarConfirm}
        onClose={handleCancelarConfirm}
        title="Cancelar Processo"
        message="Tem certeza? Os valores digitados serão perdidos."
        variant="danger"
      />
    </Card>
  );
};

export default SecaoMovimentos;
